import { Loading } from '@components/Loading'
import useSuppliersList from '@hooks/useSuppliersList'
import CheckIcon from '@mui/icons-material/Check'
import {
	Autocomplete,
	type AutocompleteRenderInputParams,
	type AutocompleteRenderOptionState,
	type AutocompleteValue,
	TextField,
	Typography,
} from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { FormGroupWrapper, SectionContent, SectionHeader } from './../styles'
import * as S from './styles'
import { SupplierFilterChip } from './SupplierFilterChip'

interface Supplier {
	id: string
	name: string
}

const getSupplierName = ({ name }: Supplier) => name
const EMPTY_ARRAY = [] as const

const renderOption = (
	props: React.HTMLAttributes<HTMLLIElement>,
	option: Supplier,
	{ selected }: AutocompleteRenderOptionState
) => (
	<S.OptionListItem {...props}>
		{option.name}
		{selected && <CheckIcon color="success" fontSize="inherit" />}
	</S.OptionListItem>
)
const isOptionEqualToValue = ({ id: optionId }: Supplier, { id }: Supplier) =>
	optionId === id

interface IProps {
	selectedSuppliers: readonly string[]
	onSuppliersListChange: (supplierIds: readonly string[]) => void
}

const SupplierListFilter: React.FC<IProps> = ({
	selectedSuppliers,
	onSuppliersListChange,
}: IProps) => {
	const { t } = useTranslation()
	const { data, loading, error } = useSuppliersList()

	const handleSuppliersListChange = useCallback(
		(
			unusedEvent: unknown,
			value: AutocompleteValue<Supplier, true, false, false>
		) => {
			onSuppliersListChange(value.map(({ id }: Supplier) => id))
		},
		[onSuppliersListChange]
	)

	const handleDeleteSupplier = useCallback(
		(deletedId: string) =>
			onSuppliersListChange(selectedSuppliers.filter(id => id !== deletedId)),
		[onSuppliersListChange, selectedSuppliers]
	)

	const selectedValues = useMemo(() => {
		const selectedIds = new Set(selectedSuppliers)
		return data?.data?.filter(({ id }) => selectedIds.has(id))
	}, [selectedSuppliers, data?.data])

	const renderInput = useCallback(
		(params: AutocompleteRenderInputParams) => (
			<TextField
				ref={params.InputProps.ref}
				inputProps={params.inputProps}
				variant="outlined"
				fullWidth
				placeholder={t('filters.label.supplierSearch')}
				autoComplete="off"
			/>
		),
		[t]
	)

	if (!data || error) {
		return (
			<FormGroupWrapper>
				<SectionHeader component="div">
					{t('formField.suppliers')}
				</SectionHeader>
				<SectionContent>
					<p>{t('error')}</p>
				</SectionContent>
			</FormGroupWrapper>
		)
	}

	if (!loading && data?.data.length < 2) {
		// It makes no sense to filter if there is 0 or only 1 supplier.
		return null
	}

	return (
		<FormGroupWrapper>
			<SectionHeader component="div">
				{t(
					'filters.label.supplier',
					selectedValues.length === 0 ||
						selectedValues.length === data?.data?.length
						? { context: 'all' }
						: { count: selectedValues.length }
				)}
			</SectionHeader>
			<SectionContent>
				{loading && (
					<div data-testid="suppliers-loading">
						<Loading />
					</div>
				)}
				{!!selectedValues && (
					<Typography>
						{selectedValues.map(supplier => (
							<SupplierFilterChip
								supplier={supplier}
								key={supplier.id}
								onDelete={handleDeleteSupplier}
							/>
						))}
					</Typography>
				)}
				{!loading && (
					<Autocomplete
						value={selectedValues}
						getOptionLabel={getSupplierName}
						multiple
						isOptionEqualToValue={isOptionEqualToValue}
						options={data?.data || EMPTY_ARRAY}
						onChange={handleSuppliersListChange}
						renderInput={renderInput}
						renderOption={renderOption}
					/>
				)}
			</SectionContent>
		</FormGroupWrapper>
	)
}

export { SupplierListFilter }
